import type {
  AgoraRTCReactError,
  ConnectionState,
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  ILocalAudioTrack,
  ILocalVideoTrack,
  IMicrophoneAudioTrack,
  IRemoteAudioTrack,
  IRemoteVideoTrack,
} from 'agora-rtc-react';
import type { MeetingWSParams } from 'store/features/event';

import type { TranscriptionMode } from './event/EventRoom';

export enum ClientRole {
  Audience = 'audience',
  Host = 'host',
}

export interface AgoraVolume {
  uid: number;
  level: number;
}

export enum MediaTrackState {
  NotReady,
  Enabled,
  Disabled,
  Enabling,
  Disabling,
}

export enum PromotionState {
  IDLE,
  PROMOTING,
  DEMOTING,
}

export type UseAgoraValue = {
  client: IAgoraRTCClient;
  connectionState: ConnectionState;
  demoteFromSpeaker: (token: string) => Promise<void>;
  localAudioTrack?: IMicrophoneAudioTrack;
  localAudioTrackError: AgoraRTCReactError | null;
  localVideoTrack?: ICameraVideoTrack;
  promoteToSpeaker(token: string): Promise<void>;
  promotionState: PromotionState;
  screensharingStream?: IAgoraRTCRemoteUser;
  speakersStreams: IAgoraRTCRemoteUser[];
  videoStream?: IAgoraRTCRemoteUser;
};

export type SendReactionParams = {
  reaction: string;
  userId: number;
};

export type AgoraCloudPlayerInfo = {
  streamUrl: string;
  createdById: number;
  createdByName: string;
  isPlaying?: boolean;
  seekPosition?: number;
  mediaPlayerUID?: number;
};

export type StartNatteringParams = {
  withBroadcast?: boolean;
  topicIds?: number[];
  questionIds?: number[];
  pace?: number;
  transcriptionMode?: TranscriptionMode;
};

interface StageInvitationData {
  invitedUserId: number;
  invitedUserFullName: string;
  invitingUserEmail: string;
  invitingUserId: number;
  invitingUserFullName: string;
  invitationTimeout: string;
}

export interface StageInvitationApiPayload {
  invitedUserId: string;
  invitedUserFullName: string;
  invitingUserEmail: string;
  invitingUserId: string;
  invitingUserFullName: string;
  invitationTimeout: string;
}

export type StageInvitationBasicInfo = Pick<
  StageInvitationData,
  'invitationTimeout' | 'invitingUserFullName' | 'invitingUserEmail'
>;

export type HandleStageInvitationParams = MeetingWSParams<Pick<StageInvitationData, 'invitingUserEmail'>>;

export interface SpeakerMediaPlayer {
  localVideoTrack?: ILocalVideoTrack;
  localAudioTrack?: ILocalAudioTrack;
  videoTrack?: IRemoteVideoTrack;
  audioTrack?: IRemoteAudioTrack;
  uid: string | number;
  isLocal: boolean;
}

export interface BroadcastSpeakerApiPayload {
  accountId: string;
  avatarLink: string;
  channelId: string;
  role: string;
  userId: string;
  userName: string;
}

export type BroadcastDemoteSpeakerApiPayload = Pick<
  BroadcastSpeakerApiPayload,
  'accountId' | 'role' | 'userId' | 'userName'
>;

export interface ScreenshareDetailsApiPayload {
  sharerUID: string;
  sharerName: string;
}

export interface ScreenshareDetails {
  sharerUID: number;
  sharerName: string;
}

export interface ActiveMeetingBroadcast {
  accessToken?: string;
  channelId: string;
  broadcastChatToken: string;
  role: ClientRole;
  isChatModerated: boolean;
  publisherAccessToken: string | null;
  screensharing?: ScreenshareDetails;
  cloudPlayer: AgoraCloudPlayerInfo | null;
  isRecording: boolean;
}

export type BroadcastConnectionInfo = Pick<
  ActiveMeetingBroadcast,
  'accessToken' | 'broadcastChatToken' | 'channelId'
> & {
  isChatModerated: 'true' | 'false';
  isRecording: 'true' | 'false';
};
